import React from "react";
import { reportActiveDashboardTutorialMetric } from "../../../utils/dashboardTutorial";
import WebProfitSpinner from "../../../components/system/WebProfitSpinner";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddBusinessRoundedIcon from "@mui/icons-material/AddBusinessRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import CallMadeRoundedIcon from "@mui/icons-material/CallMadeRounded";
import CallReceivedRoundedIcon from "@mui/icons-material/CallReceivedRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import { productsApi } from "../../../api/products";
import { projectsApi } from "../../../api/projects";
import { integrationsApi } from "../../../api/integrations";
import ResponsiveOperationalTable from "../../../components/dashboard/ResponsiveOperationalTable";
import CashProcessingExplainer from "../../../components/dashboard/erp/CashProcessingExplainer";
import CashWorksheetDialog from "../../../components/dashboard/erp/CashWorksheetDialog";
import {
  ErpEmpty,
  ErpLoading,
  ErpPanel,
} from "../../../components/dashboard/erp/ErpUi";
import {
  ACCOUNT_TYPE_LABELS,
  CASH_REGISTER_KIND_HELP,
  CASH_REGISTER_KIND_LABELS,
  ERP_BRAND,
  cashRegisterNeedsSession,
  entityId,
  formatDateTime,
  formatMoney,
  uniqueRequestKey,
} from "../../../utils/erp";
import { hasProjectPermission } from "../../../utils/projectAccess";
import { useListScrollRestoration, usePersistentListValue } from "../../../utils/listViewState";

const initialRegister = {
  code: "",
  name: "",
  accountCode: "",
  kind: "cash",
  requiresSession: true,
  currency: "ARS",
  initialBalance: "0",
  branchName: "",
  branchCode: "",
  assignedUserIds: [],
  requireAssignment: true,
  blindClosing: true,
  maxCashOutWithoutApproval: "0",
  paymentProcessing: {
    provider: "none",
    deductsFees: false,
    commissionPercent: "0",
    commissionFixed: "0",
    commissionTaxPercent: "0",
    settlementDays: "0",
    installmentFees: [
      { installments: 1, percent: "0" },
      { installments: 2, percent: "0" },
      { installments: 3, percent: "0" },
      { installments: 6, percent: "0" },
      { installments: 9, percent: "0" },
      { installments: 12, percent: "0" },
      { installments: 18, percent: "0" },
      { installments: 24, percent: "0" },
    ],
  },
  notes: "",
};
const REGISTER_PRESETS = [
  {
    key: "cash",
    label: "Efectivo",
    detail: "Plata física del local. Usa apertura y cierre de caja.",
    kind: "cash",
    name: "Efectivo",
    processor: "none",
  },
  {
    key: "bank",
    label: "Banco",
    detail: "Cuenta bancaria real: Nación, Galicia, Santander u otra.",
    kind: "bank",
    name: "Cuenta bancaria",
    processor: "none",
  },
  {
    key: "mercadopago",
    label: "Mercado Pago",
    detail: "Para cobros online y liquidaciones vinculadas a tu cuenta de Mercado Pago.",
    kind: "digital_wallet",
    name: "Mercado Pago",
    processor: "mercadopago",
  },
  {
    key: "card",
    label: "Postnet / tarjeta",
    detail: "Naranja, Posnet, Payway u otro medio que descuenta comisión o costo por cuotas.",
    kind: "card",
    name: "Postnet",
    processor: "manual",
  },
  {
    key: "digital_wallet",
    label: "Otra billetera",
    detail: "Billetera digital con comisión configurable.",
    kind: "digital_wallet",
    name: "Billetera digital",
    processor: "manual",
  },
  {
    key: "other",
    label: "Otra cuenta",
    detail: "Otro lugar real donde guardás o recibís plata.",
    kind: "other",
    name: "",
    processor: "none",
  },
];

const initialOpen = { openingAmount: "0", notes: "" };
const initialClose = { declaredClosingAmount: "", notes: "", useDenominations: false, denominationCounts: {}, otherCashAmount: "0" };
const CASH_DENOMINATIONS = [20000, 10000, 2000, 1000, 500, 200, 100, 50, 20, 10];
function cashCountTotal(form) {
  return CASH_DENOMINATIONS.reduce((total, bill) => total + bill * Math.max(0, Math.floor(Number(form.denominationCounts?.[bill]) || 0)), 0)
    + (Number(form.otherCashAmount) || 0);
}
const initialReview = {
  reviewStatus: "approved",
  reviewNotes: "",
  createAdjustment: false,
  adjustmentNotes: "",
};

const ITEM_KIND_LABELS = {
  product: "Producto",
  service: "Servicio",
  supply: "Insumo",
  expense: "Gasto",
  asset: "Activo",
  charge: "Cargo",
};

function itemKindOf(item = {}) {
  const value = String(item.itemKind || item.erpItemKind || item.tipo || "product")
    .trim()
    .toLowerCase();
  if (["servicio", "service"].includes(value)) return "service";
  if (["insumo", "supply"].includes(value)) return "supply";
  if (["gasto", "expense"].includes(value)) return "expense";
  if (["activo", "asset"].includes(value)) return "asset";
  if (["cargo", "charge"].includes(value)) return "charge";
  return "product";
}

function itemCapabilities(item = {}) {
  const kind = itemKindOf(item);
  const defaults = {
    product: { sellable: true, purchasable: true, stockTracked: true, expenseEnabled: false },
    service: { sellable: true, purchasable: false, stockTracked: false, expenseEnabled: false },
    supply: { sellable: false, purchasable: true, stockTracked: true, expenseEnabled: true },
    expense: { sellable: false, purchasable: true, stockTracked: false, expenseEnabled: true },
    asset: { sellable: false, purchasable: true, stockTracked: false, expenseEnabled: true },
    charge: { sellable: true, purchasable: true, stockTracked: false, expenseEnabled: true },
  }[kind] || {};
  return { ...defaults, ...(item.operational || item.capabilities || {}) };
}

function canSelectForDirection(item = {}, direction = "in") {
  if (item.activo === false || item.active === false) return false;
  const capabilities = itemCapabilities(item);
  return direction === "in"
    ? capabilities.sellable === true
    : capabilities.purchasable === true || capabilities.expenseEnabled === true;
}

function suggestedOperationAmount(item = {}, direction = "in") {
  if (direction === "in") {
    const price = item.precio ?? item.price;
    return Number(price || 0) > 0 ? String(Number(price)) : "";
  }
  const costing = item.costing || {};
  const method = costing.costingMethod || "last_cost";
  const methodValue = {
    last_cost: costing.lastCost,
    average_cost: costing.averageCost,
    standard_cost: costing.standardCost,
  }[method];
  const candidates = [
    methodValue,
    costing.lastCost,
    costing.averageCost,
    costing.standardCost,
    item.precio,
    item.price,
  ];
  const value = candidates.find((candidate) => Number(candidate || 0) > 0);
  return value === undefined ? "" : String(Number(value));
}

function itemTaxCode(item = {}) {
  return String(item.tax?.profileCode || item.taxProfileCode || "").trim().toUpperCase();
}

function itemDuration(item = {}) {
  return Number(item.durationMinutes ?? item.duracionMinutos ?? 0);
}

function itemOptionLabel(item = {}) {
  const code = String(item.codigo || item.code || "").trim();
  const name = String(item.nombre || item.name || "Ítem").trim();
  return code ? `${name} · ${code}` : name;
}



function cashPurposeDescription(purpose) {
  return ({
    owner_contribution: "Aporte del dueño",
    other_income: "Otro ingreso del negocio",
    owner_withdrawal: "Retiro personal del dueño",
    business_expense: "Gasto del negocio",
    employee_advance: "Entrega de fondos a rendir",
  })[purpose] || "Movimiento manual de caja";
}

function newOperationForm() {
  return {
    direction: "in",
    itemId: "",
    quantity: "1",
    amount: "",
    description: cashPurposeDescription("owner_contribution"),
    counterAccountCode: "OTHER_INCOME",
    cashPurpose: "owner_contribution",
    cashRecipient: "",
    reference: "",
    notes: "",
    idempotencyKey: uniqueRequestKey("cash-ui"),
  };
}

function memberUser(member = {}) {
  return member.userId || member.user || {};
}

function registerToForm(register = {}) {
  return {
    ...initialRegister,
    code: register.code || "",
    name: register.name || "",
    accountCode: register.accountCode || "",
    kind: register.kind || "cash",
    requiresSession: cashRegisterNeedsSession(register),
    currency: register.currency || "ARS",
    initialBalance: "0",
    branchName: register.branchName || "",
    branchCode: register.branchCode || "",
    assignedUserIds: Array.isArray(register.assignedUserIds)
      ? register.assignedUserIds.map((user) => entityId(user))
      : [],
    requireAssignment: register.requireAssignment === true,
    blindClosing: register.blindClosing !== false,
    maxCashOutWithoutApproval: String(register.maxCashOutWithoutApproval || 0),
    paymentProcessing: {
      ...initialRegister.paymentProcessing,
      ...(register.paymentProcessing || {}),
      commissionPercent: String(register.paymentProcessing?.commissionPercent || 0),
      commissionFixed: String(register.paymentProcessing?.commissionFixed || 0),
      commissionTaxPercent: String(register.paymentProcessing?.commissionTaxPercent || 0),
      settlementDays: String(register.paymentProcessing?.settlementDays || 0),
      installmentFees: [...new Set([1, 2, 3, 6, 9, 12, 18, 24, ...(register.paymentProcessing?.installmentFees || []).map((row) => Number(row.installments)).filter((n) => Number.isInteger(n) && n >= 1 && n <= 60)])].sort((a, b) => a - b).map((installments) => ({
        installments,
        percent: String(
          (register.paymentProcessing?.installmentFees || []).find((row) => Number(row.installments) === installments)?.percent || 0,
        ),
      })),
    },
    notes: register.notes || "",
  };
}

export default function ProjectErpCashPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { project, access, bootstrap, refreshBootstrap } = useOutletContext();
  const [registers, setRegisters] = React.useState([]);
  const [sessions, setSessions] = React.useState([]);
  const [cashMovements, setCashMovements] = React.useState([]);
  const [members, setMembers] = React.useState([]);
  const [blindClosing, setBlindClosing] = React.useState(access?.blindCashClosing === true);
  const [movementsTotal, setMovementsTotal] = React.useState(0);
  const [page, setPage] = usePersistentListValue(`erp-cash:${projectId}:page`, 0);
  const [rowsPerPage, setRowsPerPage] = usePersistentListValue(`erp-cash:${projectId}:page-size`, 15);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [registerDialog, setRegisterDialog] = React.useState(false);
  const [registerEditing, setRegisterEditing] = React.useState(null);
  // [WP-STABLE][CASH-REGISTER-GUIDED-CREATE]
  // STATUS: IMPLEMENTED / STATIC_PASS_TARGET / STAGING_PENDING.
  // CONTRACT: el tutorial sólo explica la configuración; crear/editar sigue usando el mismo payload y endpoint.
  const [registerCreationChoiceOpen, setRegisterCreationChoiceOpen] = React.useState(false);
  const [guidedRegisterCreation, setGuidedRegisterCreation] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deletingRegister, setDeletingRegister] = React.useState(false);
  const [registerForm, setRegisterForm] = React.useState(initialRegister);
  const [registerPreset, setRegisterPreset] = React.useState("");
  const [showInitialBalance, setShowInitialBalance] = React.useState(false);
  const [showAdvancedRegisterOptions, setShowAdvancedRegisterOptions] = React.useState(false);
  const [openTarget, setOpenTarget] = React.useState(null);
  const [openForm, setOpenForm] = React.useState(initialOpen);
  const [closeTarget, setCloseTarget] = React.useState(null);
  const [closeForm, setCloseForm] = React.useState(initialClose);
  const [worksheetSessionId, setWorksheetSessionId] = React.useState(null);
  const [operationTarget, setOperationTarget] = React.useState(null);
  const [operationForm, setOperationForm] = React.useState(newOperationForm);
  const [operationItems, setOperationItems] = React.useState([]);
  const [operationItemsLoading, setOperationItemsLoading] = React.useState(false);
  const [operationItemsError, setOperationItemsError] = React.useState("");
  const [operationSearch, setOperationSearch] = React.useState("");
  const [operationPage, setOperationPage] = React.useState(1);
  const [operationHasMore, setOperationHasMore] = React.useState(false);
  const [reviewTarget, setReviewTarget] = React.useState(null);
  const [reviewForm, setReviewForm] = React.useState(initialReview);
  const [mercadoPagoIntegration, setMercadoPagoIntegration] = React.useState(null);
  const [mercadoPagoChecked, setMercadoPagoChecked] = React.useState(false);

  const canReadRegisters = hasProjectPermission(access, "cash.registers.read");
  const canManageRegisters = hasProjectPermission(access, "cash.registers.manage");
  const canReadSessions = hasProjectPermission(access, "cash.sessions.read");
  const canReadMovements = hasProjectPermission(access, "cash.movements.read");
  const canOpen = hasProjectPermission(access, "cash.sessions.open");
  const canClose =
    hasProjectPermission(access, "cash.sessions.close_own") ||
    hasProjectPermission(access, "cash.sessions.close_any");
  const canReview = hasProjectPermission(access, "cash.sessions.review");
  const canCashIn =
    hasProjectPermission(access, "cash.operations.create") ||
    hasProjectPermission(access, "cash.operations.in");
  const canCashOut =
    hasProjectPermission(access, "cash.operations.create") ||
    hasProjectPermission(access, "cash.operations.out");
  const canOperate = canCashIn || canCashOut;
  const canCreatePurchases = hasProjectPermission(access, "erp.purchases.create");
  const canCreateExpenses = hasProjectPermission(access, "erp.expenses.create");
  const canRegisterItemIncome =
    canCashIn &&
    hasProjectPermission(access, "erp.sales.create") &&
    hasProjectPermission(access, "erp.collections.create");
  const canRegisterItemExpense =
    canCashOut &&
    hasProjectPermission(access, "erp.payments.create") &&
    (canCreatePurchases || canCreateExpenses);
  const canReadItemCosts = hasProjectPermission(access, "items.costs.read");
  const canReadExpected = hasProjectPermission(access, "cash.expected.read");
  const canCreateReviewAdjustment = hasProjectPermission(access, "erp.ledger.manage")
    && hasProjectPermission(access, "erp.movements.create");
  const canReadWorksheet = canReadSessions && canReadMovements;

  const openNewRegisterDialog = React.useCallback(() => {
    setRegisterEditing(null);
    setRegisterForm(initialRegister);
    setRegisterPreset("");
    setShowInitialBalance(false);
    setShowAdvancedRegisterOptions(false);
    setGuidedRegisterCreation(false);
    setRegisterCreationChoiceOpen(true);
    setRegisterDialog(true);
  }, []);

  const selectRegisterPreset = React.useCallback((preset) => {
    setRegisterPreset(preset.key);
    setRegisterForm((current) => ({
      ...current,
      name: preset.name,
      kind: preset.kind,
      requiresSession: preset.kind === "cash",
      paymentProcessing: {
        ...initialRegister.paymentProcessing,
        provider: preset.processor || "none",
        deductsFees: ["manual", "mercadopago"].includes(preset.processor),
      },
    }));
  }, []);

  const closeRegisterDialog = React.useCallback(() => {
    setRegisterDialog(false);
    setRegisterEditing(null);
    setRegisterForm(initialRegister);
    setRegisterPreset("");
    setShowInitialBalance(false);
    setShowAdvancedRegisterOptions(false);
    setRegisterCreationChoiceOpen(false);
    setGuidedRegisterCreation(false);
  }, []);

  const chooseNormalRegisterCreation = React.useCallback(() => {
    setGuidedRegisterCreation(false);
    setRegisterCreationChoiceOpen(false);
    reportActiveDashboardTutorialMetric("cash_register_create_mode", { mode: "normal" });
  }, []);

  const chooseGuidedRegisterCreation = React.useCallback(() => {
    setGuidedRegisterCreation(true);
    setRegisterCreationChoiceOpen(false);
    reportActiveDashboardTutorialMetric("cash_register_tutorial_started", { mode: "guided" });
  }, []);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [registerData, sessionData, movementData] = await Promise.all([
        canReadRegisters
          ? erpApi.listCashRegisters(projectId)
          : Promise.resolve({ registers: [], blindClosing: true }),
        canReadSessions
          ? erpApi.listCashSessions(projectId, { page: 1, pageSize: 30 })
          : Promise.resolve({ cashSessions: [], blindClosing: true }),
        canReadMovements
          ? erpApi.listCashMovements(projectId, {
              page: page + 1,
              pageSize: rowsPerPage,
            })
          : Promise.resolve({ cashMovements: [], total: 0 }),
      ]);
      setRegisters(registerData.registers || []);
      setSessions(sessionData.cashSessions || []);
      setCashMovements(movementData.cashMovements || []);
      setMovementsTotal(Number(movementData.total || 0));
      setBlindClosing(
        registerData.blindClosing === true ||
          sessionData.blindClosing === true ||
          access?.blindCashClosing === true,
      );
    } catch (err) {
      setError(err.message || "No se pudo cargar el módulo de caja");
    } finally {
      setLoading(false);
    }
  }, [
    projectId,
    page,
    rowsPerPage,
    canReadRegisters,
    canReadSessions,
    canReadMovements,
    access?.blindCashClosing,
  ]);

  React.useEffect(() => {
    load();
  }, [load]);

  useListScrollRestoration(`erp-cash:${projectId}`, !loading);

  React.useEffect(() => {
    if (searchParams.get("crear") !== "1" || !canManageRegisters) return;
    openNewRegisterDialog();
    const next = new URLSearchParams(searchParams);
    next.delete("crear");
    setSearchParams(next, { replace: true });
  }, [canManageRegisters, openNewRegisterDialog, searchParams, setSearchParams]);

  React.useEffect(() => {
    if (!canManageRegisters) return;
    projectsApi
      .listMembers(projectId)
      .then((data) =>
        setMembers((data.members || []).filter((member) => member.status !== "suspended")),
      )
      .catch(() => setMembers([]));
  }, [projectId, canManageRegisters]);

  React.useEffect(() => {
    if (!canManageRegisters) {
      setMercadoPagoIntegration(null);
      setMercadoPagoChecked(true);
      return;
    }
    let active = true;
    setMercadoPagoChecked(false);
    integrationsApi
      .getMercadoPago(projectId)
      .then((data) => {
        if (!active) return;
        setMercadoPagoIntegration(data?.integration || null);
      })
      .catch(() => {
        if (active) setMercadoPagoIntegration(null);
      })
      .finally(() => {
        if (active) setMercadoPagoChecked(true);
      });
    return () => { active = false; };
  }, [projectId, canManageRegisters]);

  // Consulta paginada y debounce: no se pierden ítems al superar 500.
  // Conservamos el ítem seleccionado mientras cambia el texto de búsqueda.
  React.useEffect(() => {
    if (!canOperate) {
      setOperationItems([]);
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      setOperationItemsLoading(true);
      setOperationItemsError("");
      productsApi.list(projectId, {
        picker: true, active: true, page: operationPage, pageSize: 25,
        q: operationSearch,
        ...(operationForm.direction === "in" ? { sellable: true } : {}),
      }).then((data) => {
        if (!active) return;
        const incoming = Array.isArray(data?.products) ? data.products : [];
        setOperationItems((previous) => {
          const selected = previous.find((item) => entityId(item) === String(operationForm.itemId || ""));
          const combined = operationPage === 1 ? [selected, ...incoming] : [...previous, ...incoming];
          return [...new Map(combined.filter(Boolean).map((item) => [entityId(item), item])).values()];
        });
        setOperationHasMore(data?.hasMore === true);
      }).catch((err) => {
        if (!active) return;
        if (operationPage === 1) setOperationItems((current) =>
          current.filter((item) => entityId(item) === String(operationForm.itemId || "")));
        setOperationItemsError(err?.message || "No se pudieron buscar los ítems.");
      }).finally(() => { if (active) setOperationItemsLoading(false); });
    }, 300);
    return () => { active = false; window.clearTimeout(timer); };
  }, [projectId, canOperate, operationForm.direction, operationForm.itemId, operationPage, operationSearch]);

  const sync = async () => {
    await Promise.all([load(), refreshBootstrap()]);
  };

  const createRegister = async () => {
    const isMercadoPagoRegister = registerForm.paymentProcessing?.provider === "mercadopago";
    if (!registerEditing && isMercadoPagoRegister && !mercadoPagoChecked) {
      setError("Estamos verificando la conexión de Mercado Pago. Volvé a intentar en un instante.");
      return;
    }
    if (!registerEditing && isMercadoPagoRegister && mercadoPagoIntegration?.connected !== true) {
      setError("Conectá Mercado Pago antes de crear esta caja vinculada.");
      return;
    }
    if (!registerEditing && !registerPreset) {
      setError("Elegí dónde querés registrar la plata antes de continuar.");
      return;
    }
    if (!registerForm.name.trim()) {
      setError("Poné un nombre que reconozcas, por ejemplo Efectivo o Mercado Pago.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const payload = {
        ...registerForm,
        code: registerForm.code ? registerForm.code.toUpperCase() : undefined,
        branchCode: registerForm.branchCode.toUpperCase(),
        accountCode: registerForm.accountCode ? registerForm.accountCode.toUpperCase() : undefined,
        maxCashOutWithoutApproval: Number(registerForm.maxCashOutWithoutApproval || 0),
        paymentProcessing: {
          ...registerForm.paymentProcessing,
          commissionPercent: Number(registerForm.paymentProcessing?.commissionPercent || 0),
          commissionFixed: Number(registerForm.paymentProcessing?.commissionFixed || 0),
          commissionTaxPercent: Number(registerForm.paymentProcessing?.commissionTaxPercent || 0),
          settlementDays: Number(registerForm.paymentProcessing?.settlementDays || 0),
          installmentFees: (registerForm.paymentProcessing?.installmentFees || []).map((row) => ({
            installments: Number(row.installments || 1),
            percent: Number(row.percent || 0),
          })),
        },
      };
      if (registerEditing) delete payload.initialBalance;
      else payload.initialBalance = Number(registerForm.initialBalance || 0);
      let savedRegister = registerEditing;
      if (registerEditing) {
        const result = await erpApi.updateCashRegister(projectId, entityId(registerEditing), payload);
        savedRegister = result?.register || registerEditing;
      } else {
        const result = await erpApi.createCashRegister(projectId, payload);
        savedRegister = result?.register || payload;
      }
      closeRegisterDialog();
      if (!registerEditing) {
        reportActiveDashboardTutorialMetric("cash_register_completed", {
          operationId: String(savedRegister?.id || savedRegister?._id || "cash-register"),
          registerType: String(savedRegister?.type || savedRegister?.kind || registerForm?.kind || ""),
          tutorialMode: guidedRegisterCreation ? "guided" : "normal",
          itemCount: 1,
        });
      }
      setMessage(
        registerEditing
          ? "Caja o cuenta actualizada."
          : cashRegisterNeedsSession(savedRegister)
            ? `Listo: ${savedRegister?.name || "la caja"} quedó configurada. Para usarla, abrí la caja desde esta pantalla.`
            : `Listo: ${savedRegister?.name || "la cuenta"} quedó lista para registrar movimientos.`,
      );
      await sync();
    } catch (err) {
      setError(err.message || "No se pudo crear la caja");
    } finally {
      setSaving(false);
    }
  };

  const deleteRegister = async () => {
    const register = deleteTarget?.register || deleteTarget;
    const registerId = entityId(register);
    if (!registerId) return;
    try {
      setDeletingRegister(true);
      setError("");
      setMessage("");
      const result = await erpApi.deleteCashRegister(projectId, registerId);
      setDeleteTarget(null);
      setMessage(
        result?.message ||
          (result?.archived
            ? "Caja eliminada de la operación. Su historial quedó preservado."
            : "Caja eliminada."),
      );
      await sync();
    } catch (err) {
      setError(err.message || "No se pudo eliminar la caja");
    } finally {
      setDeletingRegister(false);
    }
  };

  const openRegister = async () => {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await erpApi.openCashRegister(projectId, entityId(openTarget?.register), {
        openingAmount: Number(openForm.openingAmount || 0),
        notes: openForm.notes,
      });
      setOpenTarget(null);
      setOpenForm(initialOpen);
      setMessage("Caja abierta correctamente.");
      await sync();
    } catch (err) {
      setError(err.message || "No se pudo abrir la caja");
    } finally {
      setSaving(false);
    }
  };

  const closeRegister = async () => {
    if (closeForm.useDenominations && (
      !Number.isFinite(cashCountTotal(closeForm)) || cashCountTotal(closeForm) < 0 ||
      CASH_DENOMINATIONS.some((bill) => {
        const raw = closeForm.denominationCounts?.[bill];
        return raw !== undefined && raw !== "" && (!Number.isSafeInteger(Number(raw)) || Number(raw) < 0);
      }) || Number(closeForm.otherCashAmount || 0) < 0
    )) {
      setError("Revisá las cantidades del arqueo.");
      return;
    }
    if (closeForm.declaredClosingAmount === "") {
      setError("Contá el efectivo e ingresá el saldo declarado.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const result = await erpApi.closeCashSession(
        projectId,
        entityId(closeTarget?.openSession),
        {
          declaredClosingAmount: Number(closeForm.declaredClosingAmount || 0),
          notes: closeForm.notes,
          ...(closeForm.useDenominations ? {
            denominationCounts: Object.fromEntries(Object.entries(closeForm.denominationCounts).filter(([, value]) => value !== "").map(([bill, count]) => [bill, Number(count)])),
            otherCashAmount: Number(closeForm.otherCashAmount || 0),
          } : {}),
        },
      );
      const difference = result?.totals?.difference;
      setCloseTarget(null);
      setCloseForm(initialClose);
      setMessage(
        difference === undefined
          ? "Caja cerrada y enviada a revisión."
          : `Caja cerrada. Diferencia registrada: ${formatMoney(
              difference,
              closeTarget?.register?.currency,
            )}.`,
      );
      await sync();
    } catch (err) {
      setError(err.message || "No se pudo cerrar la caja");
    } finally {
      setSaving(false);
    }
  };

  const recordOperation = async () => {
    const selectedItem = operationItems.find(
      (item) => entityId(item) === String(operationForm.itemId || ""),
    );
    const quantity = Number(operationForm.quantity || 1);
    if (Number(operationForm.amount || 0) <= 0 || !operationForm.description.trim()) {
      setError("Completá una descripción y un importe mayor a cero.");
      return;
    }
    if (!selectedItem && ["owner_withdrawal", "employee_advance"].includes(operationForm.cashPurpose)
      && !operationForm.cashRecipient.trim()) {
      setError("Indicá quién recibe o retira el dinero.");
      return;
    }
    if (selectedItem && quantity <= 0) {
      setError("La cantidad debe ser mayor a cero.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const result = await erpApi.recordCashOperation(projectId, {
        registerId: entityId(operationTarget?.register),
        direction: operationForm.direction,
        itemId: selectedItem ? entityId(selectedItem) : undefined,
        quantity: selectedItem ? quantity : undefined,
        unitAmount: selectedItem ? Number(operationForm.amount) : undefined,
        amount: Number(operationForm.amount),
        description: operationForm.description,
        counterAccountCode: selectedItem ? undefined : operationForm.counterAccountCode,
        cashPurpose: selectedItem ? undefined : operationForm.cashPurpose,
        cashRecipient: selectedItem ? undefined : operationForm.cashRecipient,
        reference: operationForm.reference,
        notes: operationForm.notes,
        idempotencyKey: operationForm.idempotencyKey,
        metadata: selectedItem
          ? {
              itemId: entityId(selectedItem),
              itemSnapshot: {
                code: selectedItem.codigo || selectedItem.code || "",
                name: selectedItem.nombre || selectedItem.name || "",
                kind: itemKindOf(selectedItem),
                category: selectedItem.categoria || selectedItem.category || "",
                unit: selectedItem.unidad || selectedItem.unit || "",
                taxProfileCode: itemTaxCode(selectedItem),
              },
            }
          : {},
      });
      const movementTotal = Number(result?.movement?.total || 0);
      setOperationTarget(null);
      setOperationForm(newOperationForm());
      setMessage(
        selectedItem
          ? `${operationForm.direction === "in" ? "Venta cobrada" : "Compra o gasto pagado"} y registrado por ${formatMoney(
              movementTotal || Number(operationForm.amount) * quantity,
              operationTarget?.register?.currency,
            )}.`
          : "Operación manual de caja registrada.",
      );
      await sync();
    } catch (err) {
      setError(err.message || "No se pudo registrar la operación de caja");
    } finally {
      setSaving(false);
    }
  };

  const reviewSession = async () => {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await erpApi.reviewCashSession(projectId, entityId(reviewTarget), reviewForm);
      setReviewTarget(null);
      setReviewForm(initialReview);
      setMessage("Cierre revisado y auditado.");
      await sync();
    } catch (err) {
      setError(err.message || "No se pudo revisar el cierre");
    } finally {
      setSaving(false);
    }
  };

  const canUseItemSelector =
    operationForm.direction === "in" ? canRegisterItemIncome : canRegisterItemExpense;
  const selectableOperationItems = operationItems.filter((item) => {
    if (!canSelectForDirection(item, operationForm.direction)) return false;
    if (operationForm.direction === "in") return canRegisterItemIncome;
    const capabilities = itemCapabilities(item);
    const becomesPurchase = capabilities.stockTracked && capabilities.purchasable;
    if (becomesPurchase) return canCreatePurchases;
    if (capabilities.expenseEnabled) return canCreateExpenses;
    return canCreatePurchases;
  });
  const selectedOperationItem = selectableOperationItems.find(
    (item) => entityId(item) === String(operationForm.itemId || ""),
  ) || null;
  const selectedTaxProfile = selectedOperationItem
    ? (bootstrap.taxProfiles || []).find(
        (profile) => String(profile.code || "").toUpperCase() === itemTaxCode(selectedOperationItem),
      ) || null
    : null;
  const selectedCapabilities = selectedOperationItem
    ? itemCapabilities(selectedOperationItem)
    : null;
  const selectedAccountCode = selectedOperationItem
    ? operationForm.direction === "in"
      ? selectedOperationItem.accounting?.salesAccountCode || "SALES"
      : selectedCapabilities?.stockTracked
        ? selectedOperationItem.accounting?.inventoryAccountCode || "INVENTORY"
        : selectedOperationItem.accounting?.expenseAccountCode ||
          selectedOperationItem.accounting?.purchaseAccountCode ||
          "OTHER_EXPENSE"
    : operationForm.counterAccountCode;
  const selectedAccount = (bootstrap.accounts || []).find(
    (account) => account.code === selectedAccountCode,
  );
  const quantityValue = Math.max(1, Number(operationForm.quantity || 1));
  const unitAmountValue = Math.max(0, Number(operationForm.amount || 0));
  const taxRate = Number(selectedTaxProfile?.rate ?? selectedOperationItem?.tax?.rate ?? 0);
  const taxMode = String(
    selectedTaxProfile?.calculationMode || selectedOperationItem?.tax?.calculationMode || "percentage",
  ).toLowerCase();
  const taxIncluded = Boolean(
    selectedTaxProfile?.includedInPrice ?? selectedOperationItem?.tax?.includedInPrice,
  );
  const baseOperationAmount = unitAmountValue * quantityValue;
  const estimatedTax = taxRate <= 0
    ? 0
    : taxMode === "fixed"
      ? taxRate
      : taxIncluded
        ? baseOperationAmount - baseOperationAmount / (1 + taxRate / 100)
        : baseOperationAmount * (taxRate / 100);
  const estimatedOperationTotal = taxIncluded
    ? baseOperationAmount
    : baseOperationAmount + estimatedTax;

  if (loading && !registers.length) {
    return <ErpLoading label="Cargando cajas y arqueos..." />;
  }

  return (
    <Stack spacing={2.5}>
      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      {message ? (
        <Alert severity="success" onClose={() => setMessage("")}>
          {message}
        </Alert>
      ) : null}

      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={1}
        sx={{ minHeight: 36 }}
      >
        {blindClosing ? <Chip size="small" label="Cierre ciego" variant="outlined" /> : null}
        <Button
          size="small"
          variant="text"
          startIcon={loading ? <WebProfitSpinner size={15} decorative /> : <RefreshRoundedIcon />}
          onClick={load}
          disabled={loading}
          sx={{ textTransform: "none", fontWeight: 800 }}
        >
          Actualizar
        </Button>
        {canManageRegisters ? (
          <Button
            data-wp-tutorial-target="cash-create"
            size="small"
            variant="contained"
            startIcon={<AddBusinessRoundedIcon />}
            onClick={openNewRegisterDialog}
            sx={{ textTransform: "none", fontWeight: 900, boxShadow: "none" }}
          >
            Nueva caja
          </Button>
        ) : null}
      </Stack>

      {/* [WP-STABLE][CASH-ROLE-VIEW] Atajos operativos sin alterar el POS ni la estructura ERP. */}
      {access?.role === "cajero" && canReadRegisters ? (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AccountBalanceWalletRoundedIcon color="primary" />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 950 }}>Mi turno de caja</Typography>
                <Typography variant="body2" color="text.secondary">Abrí tu caja, registrá operaciones y terminá con un arqueo de efectivo.</Typography>
              </Box>
            </Stack>
            {!registers.some((row) => cashRegisterNeedsSession(row.register)) ? (
              <Alert severity="warning">No tenés una caja física asignada. Pedile al administrador que te habilite una caja en Equipo y accesos.</Alert>
            ) : (
              <Stack spacing={1}>
                {registers.filter((row) => cashRegisterNeedsSession(row.register)).map((row) => {
                  const occupied = row.occupied === true || Boolean(row.openSession);
                  return (
                    <Stack key={entityId(row.register)} direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" spacing={1} sx={{ p: 1.5, border: `1px solid ${ERP_BRAND.border}`, borderRadius: 2 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 850 }}>{row.register.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{occupied ? (row.occupiedByOther ? "Ocupada por otro empleado" : `Abierta desde ${formatDateTime(row.openSession?.openedAt)}`) : "Sin turno abierto"}</Typography>
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {!occupied && canOpen ? <Button variant="contained" size="small" onClick={() => { setOpenTarget(row); setOpenForm(initialOpen); }}>Abrir caja</Button> : null}
                        {row.openSession && canReadWorksheet ? <Button variant="outlined" size="small" onClick={() => setWorksheetSessionId(entityId(row.openSession))}>Ver planilla</Button> : null}
                        {row.openSession && row.canCloseSession && canClose ? <Button color="warning" size="small" onClick={() => { setCloseTarget(row); setCloseForm(initialClose); }}>Cerrar turno</Button> : null}
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </Paper>
      ) : null}

      {canReview && canReadRegisters ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} justifyContent="space-between">
            <Box><Typography sx={{ fontWeight: 900 }}>Supervisión de cajas</Typography><Typography variant="body2" color="text.secondary">Turnos abiertos, pendientes y control de arqueos.</Typography></Box>
            <Stack direction="row" spacing={1}>
              <Chip variant="outlined" color="success" label={`${registers.filter((row) => row.occupied || row.openSession).length} cajas abiertas`} />
              <Chip variant="outlined" color={sessions.some((row) => row.reviewStatus === "pending") ? "warning" : "default"} label={`${sessions.filter((row) => row.reviewStatus === "pending").length} cierres por revisar`} />
            </Stack>
          </Stack>
        </Paper>
      ) : null}

      {!registers.length ? (
        <Box sx={{ py: 5, borderTop: `1px solid ${ERP_BRAND.border}`, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
          <ErpEmpty
            title="Todavía no hay cajas"
            description={canManageRegisters ? "Creá el lugar real donde entra o sale el dinero." : "No tenés cajas asignadas o todavía no se configuró ninguna."}
            action={canManageRegisters ? (
              <Button data-wp-tutorial-target="cash-create" variant="contained" startIcon={<AddBusinessRoundedIcon />} onClick={openNewRegisterDialog}>
                Crear caja
              </Button>
            ) : null}
          />
        </Box>
      ) : (
        <Box sx={{ borderTop: `1px solid ${ERP_BRAND.border}`, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
          <ResponsiveOperationalTable
            size="small"
            mobileLabel="Cajas y cuentas"
            mobileEssentialLabels={["Cuenta", "Estado", "Saldo", "Costos"]}
            sx={{
              "& .MuiTableCell-root": { borderColor: ERP_BRAND.border, py: 1.15, px: 1.1 },
              "& .MuiTableCell-head": { color: ERP_BRAND.muted, fontSize: 12, fontWeight: 900 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Cuenta</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Saldo</TableCell>
                <TableCell>Costos de cobro</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {registers.map((item) => {
                const register = item.register;
                const isOpen = item.occupied === true || Boolean(item.openSession);
                const isOwnSession = Boolean(item.openSession);
                const requiresSession = cashRegisterNeedsSession(register);
                const usable = !requiresSession || isOpen;
                const totalsHidden = item.totals?.hidden === true || !canReadExpected;
                const processing = register.paymentProcessing || {};
                const processorLabel = processing.provider === "mercadopago"
                  ? (!canManageRegisters
                    ? "Mercado Pago"
                    : mercadoPagoChecked && mercadoPagoIntegration?.connected
                      ? "Mercado Pago · API conectada"
                      : mercadoPagoChecked
                        ? "Mercado Pago · falta conexión"
                        : "Mercado Pago · verificando")
                  : processing.provider === "manual" && processing.deductsFees
                    ? `${Number(processing.commissionPercent || 0).toLocaleString("es-AR")}% comisión${(processing.installmentFees || []).some((row) => Number(row.percent || 0) > 0) ? " + cuotas" : ""}`
                    : "Sin costos configurados";
                const balance = requiresSession
                  ? (totalsHidden ? null : item.totals?.expected)
                  : (item.balance?.hidden ? null : item.balance?.balance);
                return (
                  <TableRow key={entityId(register)} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 900, color: ERP_BRAND.dark, lineHeight: 1.2 }}>{register.name}</Typography>
                      {register.branchName ? <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{register.branchName}</Typography> : null}
                      {register.requiresSession !== false && register.blindClosing ? <Typography variant="caption" sx={{ display: "block", color: ERP_BRAND.muted }}>Arqueo ciego</Typography> : null}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{CASH_REGISTER_KIND_LABELS[register.kind] || "Otra"}</Typography>
                      <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{register.currency}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={usable ? "success" : "default"}
                        label={requiresSession ? (isOpen ? (item.occupiedByOther ? "Ocupada por otro" : "Abierta") : "Cerrada") : "Lista"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 900 }}>{balance === null || balance === undefined ? "—" : formatMoney(balance, register.currency)}</Typography>
                      {requiresSession && isOpen && !totalsHidden ? (
                        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Ingresos {formatMoney(item.totals?.incoming, register.currency)}</Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: processing.provider === "none" ? 500 : 800 }}>{processorLabel}</Typography>
                      {processing.provider === "manual" && Number(processing.settlementDays || 0) > 0 ? (
                        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Acredita aprox. en {processing.settlementDays} días</Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.4} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                        {requiresSession && !isOpen && canOpen ? (
                          <Button size="small" onClick={() => { setOpenTarget(item); setOpenForm(initialOpen); }} sx={{ textTransform: "none", fontWeight: 850 }}>Abrir</Button>
                        ) : null}
                        {usable && canOperate && !item.occupiedByOther ? (
                          <>
                            <Button size="small" variant="contained" onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/movimientos?nuevo=1`)} sx={{ textTransform: "none", fontWeight: 850, boxShadow: "none" }}>Registrar</Button>
                            <Button size="small" variant="outlined" onClick={() => {
                              setOperationTarget(item);
                              const next = newOperationForm();
                              if (!canCashIn && canCashOut) {
                                next.direction = "out";
                                next.cashPurpose = "owner_withdrawal";
                                next.description = cashPurposeDescription("owner_withdrawal");
                                next.counterAccountCode = "OTHER_EXPENSE";
                              }
                              setOperationForm(next);
                              setOperationSearch("");
                              setOperationPage(1);
                              setError("");
                            }} sx={{ textTransform: "none", fontWeight: 850 }}>Ingresar / retirar</Button>
                          </>
                        ) : null}
                        {requiresSession && isOwnSession && item.canCloseSession && canClose ? (
                          <Button size="small" color="warning" onClick={() => { setCloseTarget(item); setCloseForm(initialClose); }} sx={{ textTransform: "none", fontWeight: 850 }}>Cerrar</Button>
                        ) : null}
                        {canReadWorksheet && isOwnSession ? (
                          <Button size="small" startIcon={<ReceiptLongRoundedIcon />} onClick={() => setWorksheetSessionId(entityId(item.openSession))} sx={{ textTransform: "none", fontWeight: 800 }}>Planilla</Button>
                        ) : null}
                        {canManageRegisters ? (
                          <Button
                            size="small"
                            disabled={isOpen}
                            title={isOpen ? "Cerrá el turno antes de cambiar la configuración" : "Editar caja o cuenta"}
                            onClick={() => {
                              setRegisterEditing(register);
                              setRegisterForm(registerToForm(register));
                              setRegisterPreset("");
                              setShowInitialBalance(false);
                              setShowAdvancedRegisterOptions(false);
                              setRegisterCreationChoiceOpen(false);
                              setGuidedRegisterCreation(false);
                              setRegisterDialog(true);
                            }}
                            sx={{ textTransform: "none", fontWeight: 800 }}
                          >
                            Editar
                          </Button>
                        ) : null}
                        {canManageRegisters ? (
                          <Button size="small" color="error" disabled={isOpen} onClick={() => setDeleteTarget(item)} sx={{ minWidth: 0, px: .5, textTransform: "none" }}>×</Button>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </ResponsiveOperationalTable>
        </Box>
      )}

      <Grid container spacing={2.5}>
        {canReadMovements ? (
          <Grid item xs={12} xl={7.5}>
            <ErpPanel>
              <Box sx={{ p: 2.5, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
                <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
                  Movimientos de caja
                </Typography>
                <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.35 }}>
                  Cobros, pagos y movimientos directos que podés consultar.
                </Typography>
              </Box>
              {!cashMovements.length ? (
                <ErpEmpty
                  title="Sin movimientos de caja"
                  description="Los cobros, pagos e ingresos directos aparecerán aquí."
                />
              ) : (
                <>
                  <TableContainer>
                    <ResponsiveOperationalTable mobileLabel="Movimientos de caja" collapseAfter={3}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Movimiento</TableCell>
                          <TableCell>Caja</TableCell>
                          <TableCell>Fecha</TableCell>
                          <TableCell align="right">Importe</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cashMovements.map((movement) => (
                          <TableRow key={entityId(movement)} hover>
                            <TableCell>
                              <Typography sx={{ fontWeight: 850 }}>
                                {movement.cashMovementNumber}
                              </Typography>
                              <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                                {movement.description}
                              </Typography>
                            </TableCell>
                            <TableCell>{movement.registerId?.name || "—"}</TableCell>
                            <TableCell>{formatDateTime(movement.occurredAt)}</TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 950,
                                color:
                                  movement.direction === "in"
                                    ? "success.main"
                                    : movement.direction === "out"
                                      ? "error.main"
                                      : ERP_BRAND.dark,
                              }}
                            >
                              {movement.direction === "in"
                                ? "+"
                                : movement.direction === "out"
                                  ? "-"
                                  : ""}
                              {formatMoney(movement.amount, movement.currency)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </ResponsiveOperationalTable>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={movementsTotal}
                    page={page}
                    onPageChange={(_event, nextPage) => setPage(nextPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(Number(event.target.value));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 15, 25, 50]}
                    labelRowsPerPage="Filas por página"
                  />
                </>
              )}
            </ErpPanel>
          </Grid>
        ) : null}

        {canReadSessions ? (
          <Grid item xs={12} xl={canReadMovements ? 4.5 : 12}>
            <ErpPanel>
              <Box sx={{ p: 2.5, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
                <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
                  Últimas sesiones
                </Typography>
                <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.35 }}>
                  Aperturas, cierres y diferencias recientes.
                </Typography>
              </Box>
              {!sessions.length ? (
                <ErpEmpty
                  title="Sin sesiones"
                  description="Cuando abras una caja se generará la primera sesión."
                />
              ) : (
                <Stack spacing={0} sx={{ px: 2.5 }}>
                  {sessions.slice(0, 15).map((session, index) => (
                    <Box
                      key={entityId(session)}
                      sx={{ py: 1.8, borderTop: index ? `1px solid ${ERP_BRAND.border}` : "none" }}
                    >
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Box>
                          <Typography sx={{ fontWeight: 900 }}>
                            {session.registerId?.name || "Caja"}
                          </Typography>
                          <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                            {session.sessionNumber} · {formatDateTime(session.openedAt)}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          color={session.status === "open" ? "success" : "default"}
                          label={session.status === "open" ? "Abierta" : "Cerrada"}
                        />
                      </Stack>
                      {session.status === "closed" ? (
                        <Stack spacing={0.8} sx={{ mt: 1 }}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                              Declarado
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 900 }}>
                              {formatMoney(session.declaredClosingAmount, session.currency)}
                            </Typography>
                          </Stack>
                          {session.difference !== undefined && session.difference !== null ? (
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                                Diferencia
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 900,
                                  color:
                                    Number(session.difference) === 0
                                      ? "success.main"
                                      : "warning.main",
                                }}
                              >
                                {formatMoney(session.difference, session.currency)}
                              </Typography>
                            </Stack>
                          ) : null}
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Chip
                              size="small"
                              label={
                                session.reviewStatus === "approved"
                                  ? "Aprobado"
                                  : session.reviewStatus === "observed"
                                    ? "Observado"
                                    : session.reviewStatus === "pending"
                                      ? "Pendiente de revisión"
                                      : "Sin revisión"
                              }
                              color={session.reviewStatus === "approved" ? "success" : "default"}
                            />
                            {canReview && session.reviewStatus !== "approved" ? (
                              <Button
                                size="small"
                                startIcon={<FactCheckRoundedIcon />}
                                onClick={() => {
                                  setReviewTarget(session);
                                  setReviewForm(initialReview);
                                }}
                                sx={{ textTransform: "none", fontWeight: 850 }}
                              >
                                Revisar
                              </Button>
                            ) : null}
                          </Stack>
                        </Stack>
                      ) : null}
                      {canReadWorksheet ? <Button size="small" startIcon={<ReceiptLongRoundedIcon />} onClick={() => setWorksheetSessionId(entityId(session))} sx={{ mt: 1, textTransform: "none", fontWeight: 800 }}>Ver planilla</Button> : null}
                    </Box>
                  ))}
                </Stack>
              )}
            </ErpPanel>
          </Grid>
        ) : null}
      </Grid>

      <CashWorksheetDialog projectId={projectId} cashSessionId={worksheetSessionId} onClose={() => setWorksheetSessionId(null)} />

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={deletingRegister ? undefined : () => setDeleteTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 950 }}>Eliminar caja o cuenta</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={1.4} sx={{ pt: 1 }}>
            <Typography>
              ¿Querés eliminar <strong>{deleteTarget?.register?.name || deleteTarget?.name || "esta caja"}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Si ya tuvo movimientos o cierres, WebProfit la va a sacar de la operación pero conservará el historial contable. Si está abierta, primero vas a tener que cerrarla.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deletingRegister} sx={{ textTransform: "none", fontWeight: 800 }}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            startIcon={deletingRegister ? <WebProfitSpinner size={16} decorative /> : <DeleteOutlineRoundedIcon />}
            onClick={deleteRegister}
            disabled={deletingRegister}
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            {deletingRegister ? "Eliminando…" : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={registerDialog}
        onClose={saving ? undefined : closeRegisterDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 950 }}>
          {registerEditing ? "Configurar caja o cuenta" : "¿Dónde querés que WebProfit registre la plata?"}
        </DialogTitle>
        <Divider />
        <DialogContent>
          {registerCreationChoiceOpen && !registerEditing ? (
            <Stack spacing={2.1} sx={{ py: { xs: 1, sm: 2 }, maxWidth: 850, mx: "auto" }}>
              <Box sx={{ textAlign: "center", px: { xs: .5, sm: 2 } }}>
                <Typography variant="overline" sx={{ color: ERP_BRAND.orange, fontWeight: 1000, letterSpacing: ".12em" }}>
                  PASO 0 · CREÁ TU CAJA O CUENTA
                </Typography>
                <Typography sx={{ mt: .45, fontSize: { xs: 24, sm: 31 }, lineHeight: 1.08, fontWeight: 1000, color: ERP_BRAND.dark }}>
                  Primero decile a WebProfit dónde vive tu dinero
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, color: ERP_BRAND.muted, maxWidth: 680, mx: "auto", lineHeight: 1.55 }}>
                  Una caja no es sólo “un medio de pago”: representa un lugar real con saldo propio. Separá efectivo, bancos, Mercado Pago, tarjetas y otras billeteras para que cobros, pagos, retiros y transferencias queden en el lugar correcto.
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,minmax(0,1fr))" }, gap: 1.4 }}>
                <ButtonBase type="button" onClick={chooseNormalRegisterCreation} sx={{ borderRadius: 4, textAlign: "left", alignItems: "stretch", border: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.14)", bgcolor: "var(--wp-panel-surface,#fff)", overflow: "hidden", "&:hover": { transform: "translateY(-2px)", borderColor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.32)" } }}>
                  <Stack spacing={1.35} sx={{ p: { xs: 2.1, sm: 2.6 }, minHeight: { md: 230 }, width: "100%" }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.06)", color: ERP_BRAND.dark }}><AddBusinessRoundedIcon /></Box>
                    <Box><Typography sx={{ fontSize: 22, fontWeight: 1000, color: ERP_BRAND.dark }}>Crear normalmente</Typography><Typography variant="body2" sx={{ mt: .6, color: ERP_BRAND.muted, lineHeight: 1.5 }}>Elegí Efectivo, Banco, Mercado Pago u otra cuenta y completá la configuración directamente.</Typography></Box>
                    <Stack direction="row" spacing={.6} alignItems="center" sx={{ mt: "auto !important" }}><Typography sx={{ fontWeight: 950 }}>Ir a la configuración</Typography><ArrowForwardRoundedIcon fontSize="small" /></Stack>
                  </Stack>
                </ButtonBase>
                <ButtonBase type="button" onClick={chooseGuidedRegisterCreation} sx={{ borderRadius: 4, textAlign: "left", alignItems: "stretch", border: "1px solid rgba(243,90,58,.48)", bgcolor: "rgba(243,90,58,.055)", overflow: "hidden", "&:hover": { transform: "translateY(-2px)", borderColor: ERP_BRAND.orange } }}>
                  <Stack spacing={1.35} sx={{ p: { xs: 2.1, sm: 2.6 }, minHeight: { md: 230 }, width: "100%" }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.14)", color: ERP_BRAND.orange }}><AutoAwesomeRoundedIcon /></Box>
                    <Box><Typography sx={{ fontSize: 22, fontWeight: 1000, color: ERP_BRAND.dark }}>Usar tutorial</Typography><Typography variant="body2" sx={{ mt: .6, color: ERP_BRAND.muted, lineHeight: 1.5 }}>Te explicamos qué representa cada tipo, cuándo usar saldo inicial, por qué el efectivo abre/cierra turno y cómo impactan cobros, retiros y transferencias.</Typography></Box>
                    <Stack direction="row" spacing={.6} alignItems="center" sx={{ mt: "auto !important", color: ERP_BRAND.orange }}><Typography sx={{ fontWeight: 1000 }}>Aprender mientras la creo</Typography><ArrowForwardRoundedIcon fontSize="small" /></Stack>
                  </Stack>
                </ButtonBase>
              </Box>
            </Stack>
          ) : null}

          <Box sx={{ display: registerCreationChoiceOpen && !registerEditing ? "none" : "block" }}>
          <Stack spacing={2.2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {registerEditing
                ? "Actualizá el nombre o la configuración de este lugar."
                : "Elegí el lugar real donde queda el dinero cuando cobrás una venta."}
            </Typography>

            {guidedRegisterCreation && !registerEditing ? (
              <Alert severity="info" icon={<AccountBalanceWalletRoundedIcon />}>
                <strong>Regla simple:</strong> creá una caja/cuenta por cada lugar que realmente necesites distinguir. “Efectivo del local”, “Banco”, “Mercado Pago” y “Postnet” no deberían mezclarse si después querés saber cuánto dinero hay en cada uno.
              </Alert>
            ) : null}

            {!registerEditing ? (
              <Grid container spacing={1.2}>
                {REGISTER_PRESETS.map((preset) => {
                  const selected = registerPreset === preset.key;
                  return (
                    <Grid item xs={12} sm={6} key={preset.key}>
                      <Button
                        fullWidth
                        variant={selected ? "contained" : "outlined"}
                        onClick={() => selectRegisterPreset(preset)}
                        sx={{
                          minHeight: 78,
                          justifyContent: "flex-start",
                          textAlign: "left",
                          textTransform: "none",
                          borderRadius: 2.5,
                          px: 1.8,
                          boxShadow: "none",
                        }}
                      >
                        <Stack spacing={0.25} alignItems="flex-start">
                          <Typography component="span" sx={{ fontWeight: 950 }}>
                            {preset.label}
                          </Typography>
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ color: selected ? "inherit" : "text.secondary", lineHeight: 1.25 }}
                          >
                            {preset.detail}
                          </Typography>
                        </Stack>
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            ) : null}

            {guidedRegisterCreation && !registerEditing && registerPreset ? (
              <Alert severity="success" sx={{ borderRadius: 2.3 }}>
                {registerPreset === "cash"
                  ? "Efectivo representa plata física. Esta caja usa apertura/cierre de turno: la apertura es el efectivo contado que recibe el turno, no el saldo histórico total del negocio."
                  : registerPreset === "bank"
                    ? "Banco representa una cuenta bancaria real. No necesita apertura de turno: cobros, pagos y transferencias actualizan directamente su saldo dentro de WebProfit."
                    : registerPreset === "mercadopago"
                      ? "Mercado Pago debe quedar separado para poder conciliar cobros online, acreditaciones y costos del proveedor sin mezclarlos con efectivo o banco."
                      : registerPreset === "card"
                        ? "Postnet/tarjeta sirve cuando el cobro tiene comisiones o costos por cuotas. WebProfit puede estimarlos sin confundir el bruto cobrado con el neto que termina acreditándose."
                        : "Esta cuenta representa otro lugar real donde guardás o recibís dinero. Usala sólo si necesitás ver su saldo separado del resto."}
              </Alert>
            ) : null}

            {(registerEditing || registerPreset) ? (
              <Stack spacing={1.6}>
                {registerForm.paymentProcessing?.provider === "mercadopago" ? (
                  <Alert
                    severity={mercadoPagoIntegration?.connected ? "success" : "warning"}
                    action={!mercadoPagoIntegration?.connected ? (
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => navigate(`/dashboard/proyectos/${projectId}/configuracion/ecommerce`)}
                        sx={{ textTransform: "none", fontWeight: 900 }}
                      >
                        Conectar Mercado Pago
                      </Button>
                    ) : null}
                  >
                    {mercadoPagoIntegration?.connected
                      ? "Mercado Pago está conectado. Esta caja queda identificada para conciliar cobros online y sus costos reales."
                      : "Todavía no estás conectado con Mercado Pago. Si vas a vender por e-commerce, conectalo primero y después creá acá la caja de Mercado Pago."}
                  </Alert>
                ) : null}

                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={registerEditing || registerPreset === "other" ? 7 : 12}>
                    <TextField
                      label="Nombre que reconocés"
                      required
                      value={registerForm.name}
                      onChange={(event) => setRegisterForm((current) => ({ ...current, name: event.target.value }))}
                      placeholder="Ej.: Efectivo, Banco Nación, Mercado Pago"
                      autoFocus
                      fullWidth
                    />
                  </Grid>
                  {(registerEditing || registerPreset === "other") ? (
                    <Grid item xs={12} md={5}>
                      <TextField
                        select
                        label="Qué tipo de lugar es"
                        value={registerForm.kind}
                        onChange={(event) => {
                          const kind = event.target.value;
                          setRegisterForm((current) => ({
                            ...current,
                            kind,
                            requiresSession: kind === "cash",
                          }));
                        }}
                        helperText={CASH_REGISTER_KIND_HELP[registerForm.kind]}
                        fullWidth
                      >
                        {Object.entries(CASH_REGISTER_KIND_LABELS).map(([value, label]) => (
                          <MenuItem key={value} value={value}>{label}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  ) : null}
                </Grid>

                {["bank", "card", "digital_wallet"].includes(registerForm.kind) && registerForm.paymentProcessing?.provider !== "mercadopago" ? (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="¿Este medio descuenta costos de cobro?"
                    value={registerForm.paymentProcessing?.provider === "manual" ? "manual" : "none"}
                    onChange={(event) => setRegisterForm((current) => ({
                      ...current,
                      paymentProcessing: {
                        ...current.paymentProcessing,
                        provider: event.target.value,
                        deductsFees: event.target.value === "manual",
                      },
                    }))}
                    helperText="Una cuenta bancaria normal suele no cobrar comisión por cada venta. Activá costos manuales sólo si el banco o procesador efectivamente los descuenta."
                  >
                    <MenuItem value="none">No: registrar importe completo</MenuItem>
                    <MenuItem value="manual">Sí: calcular comisiones y cuotas estimadas</MenuItem>
                  </TextField>
                ) : null}

                {registerForm.paymentProcessing?.provider === "manual" ? (
                  <Box sx={{ pt: .3 }}>
                    <Typography sx={{ fontWeight: 900, color: ERP_BRAND.dark, mb: .35 }}>¿Cuánto te descuenta este medio?</Typography>
                    <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mb: 1.2 }}>
                      WebProfit usa estos valores como referencia en cada venta. Después podés corregir el costo real de una operación puntual.
                    </Typography>
                    <Grid container spacing={1.2}>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Comisión %"
                          value={registerForm.paymentProcessing.commissionPercent}
                          onChange={(event) => setRegisterForm((current) => ({
                            ...current,
                            paymentProcessing: { ...current.paymentProcessing, commissionPercent: event.target.value, deductsFees: true },
                          }))}
                          inputProps={{ min: 0, max: 100, step: .01 }}
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Comisión fija"
                          value={registerForm.paymentProcessing.commissionFixed}
                          onChange={(event) => setRegisterForm((current) => ({
                            ...current,
                            paymentProcessing: { ...current.paymentProcessing, commissionFixed: event.target.value, deductsFees: true },
                          }))}
                          inputProps={{ min: 0, step: .01 }}
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Impuesto s/comisión %"
                          value={registerForm.paymentProcessing.commissionTaxPercent}
                          onChange={(event) => setRegisterForm((current) => ({
                            ...current,
                            paymentProcessing: { ...current.paymentProcessing, commissionTaxPercent: event.target.value, deductsFees: true },
                          }))}
                          inputProps={{ min: 0, max: 100, step: .01 }}
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Acredita en días"
                          value={registerForm.paymentProcessing.settlementDays}
                          onChange={(event) => setRegisterForm((current) => ({
                            ...current,
                            paymentProcessing: { ...current.paymentProcessing, settlementDays: event.target.value },
                          }))}
                          inputProps={{ min: 0, max: 365, step: 1 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ fontWeight: 900 }}>Costo adicional sobre el bruto según cuotas</Typography>
                      </Grid>
                      {(registerForm.paymentProcessing.installmentFees || []).map((row, index) => (
                        <Grid item xs={6} md={3} key={row.installments}>
                          <TextField
                            fullWidth
                            type="number"
                            label={`${row.installments} cuota${row.installments === 1 ? "" : "s"} · costo %`}
                            value={row.percent}
                            onChange={(event) => setRegisterForm((current) => ({
                              ...current,
                              paymentProcessing: {
                                ...current.paymentProcessing,
                                deductsFees: true,
                                installmentFees: current.paymentProcessing.installmentFees.map((item, itemIndex) => (
                                  itemIndex === index ? { ...item, percent: event.target.value } : item
                                )),
                              },
                            }))}
                            inputProps={{ min: 0, max: 100, step: .01 }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                    <CashProcessingExplainer project={project} processing={registerForm.paymentProcessing} currency={registerForm.currency} />
                  </Box>
                ) : null}

                {!registerEditing ? (
                  <Box>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setShowInitialBalance((current) => !current)}
                      sx={{ textTransform: "none", fontWeight: 850, px: 0 }}
                    >
                      {showInitialBalance ? "Ocultar saldo inicial" : "Ya había plata acá al empezar"}
                    </Button>
                    {showInitialBalance ? (
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          type="number"
                          label="Cuánto había al empezar"
                          value={registerForm.initialBalance}
                          onChange={(event) => setRegisterForm((current) => ({ ...current, initialBalance: event.target.value }))}
                          inputProps={{ min: 0, step: "any" }}
                          helperText={guidedRegisterCreation ? "Saldo inicial = lo que ya existía en esta cuenta cuando empezaste a usar WebProfit. No es una venta ni un aporte nuevo. Si no había plata, dejalo en 0." : "Si no había plata o preferís cargarla después, dejalo en 0."}
                          fullWidth
                        />
                      </Box>
                    ) : null}
                  </Box>
                ) : null}

                <Box>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setShowAdvancedRegisterOptions((current) => !current)}
                    sx={{ textTransform: "none", fontWeight: 850, px: 0 }}
                  >
                    {showAdvancedRegisterOptions ? "Ocultar opciones avanzadas" : "Opciones avanzadas"}
                  </Button>

                  {showAdvancedRegisterOptions ? (
                    <Stack spacing={1.6} sx={{ mt: 1.2 }}>
                      <Alert severity="info">
                        Estas opciones son administrativas. No necesitás cambiarlas para empezar a vender.
                      </Alert>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Moneda"
                            value={registerForm.currency}
                            onChange={(event) => setRegisterForm((current) => ({ ...current, currency: event.target.value.toUpperCase() }))}
                            placeholder="ARS"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Código de sucursal"
                            value={registerForm.branchCode}
                            onChange={(event) =>
                              setRegisterForm((current) => ({
                                ...current,
                                branchCode: event.target.value.toUpperCase(),
                              }))
                            }
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Sucursal o ubicación"
                            value={registerForm.branchName}
                            onChange={(event) =>
                              setRegisterForm((current) => ({
                                ...current,
                                branchName: event.target.value,
                              }))
                            }
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            type="number"
                            label="Máximo egreso sin aprobación"
                            value={registerForm.maxCashOutWithoutApproval}
                            onChange={(event) =>
                              setRegisterForm((current) => ({
                                ...current,
                                maxCashOutWithoutApproval: event.target.value,
                              }))
                            }
                            inputProps={{ min: 0, step: "any" }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            select
                            SelectProps={{ multiple: true }}
                            label="Personas asignadas"
                            value={registerForm.assignedUserIds}
                            onChange={(event) =>
                              setRegisterForm((current) => ({
                                ...current,
                                assignedUserIds:
                                  typeof event.target.value === "string"
                                    ? event.target.value.split(",")
                                    : event.target.value,
                              }))
                            }
                            helperText="Usalo si sólo algunas personas deben operar este lugar."
                            fullWidth
                          >
                            {members.map((member) => {
                              const person = memberUser(member);
                              return (
                                <MenuItem key={entityId(member)} value={entityId(person)}>
                                  {person.name || person.email} · {member.role}
                                </MenuItem>
                              );
                            })}
                          </TextField>
                        </Grid>
                      </Grid>

                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={registerForm.requireAssignment}
                              onChange={(event) =>
                                setRegisterForm((current) => ({
                                  ...current,
                                  requireAssignment: event.target.checked,
                                }))
                              }
                            />
                          }
                          label="Exigir asignación para operar"
                        />
                        {registerForm.requiresSession ? (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={registerForm.blindClosing}
                                onChange={(event) => setRegisterForm((current) => ({ ...current, blindClosing: event.target.checked }))}
                              />
                            }
                            label="Ocultar saldo esperado durante el cierre"
                          />
                        ) : null}
                      </Stack>

                      <TextField
                        label="Notas"
                        multiline
                        minRows={2}
                        value={registerForm.notes}
                        onChange={(event) =>
                          setRegisterForm((current) => ({ ...current, notes: event.target.value }))
                        }
                        fullWidth
                      />
                    </Stack>
                  ) : null}
                </Box>
              </Stack>
            ) : null}
          </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, display: registerCreationChoiceOpen && !registerEditing ? "none" : "flex" }}>
          <Button onClick={closeRegisterDialog} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={createRegister}
            disabled={
              saving
                || (!registerEditing && (!registerPreset || !registerForm.name.trim()))
                || (!registerEditing
                  && registerForm.paymentProcessing?.provider === "mercadopago"
                  && (!mercadoPagoChecked || mercadoPagoIntegration?.connected !== true))
            }
            startIcon={saving ? <WebProfitSpinner size={16} /> : undefined}
          >
            {saving ? "Guardando..." : registerEditing ? "Guardar cambios" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(openTarget)}
        onClose={saving ? undefined : () => setOpenTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 950 }}>Abrir {openTarget?.register?.name}</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              Esto abre el turno de trabajo de la caja física. No es el saldo inicial contable del negocio: ingresá únicamente el efectivo contado que recibe la persona al comenzar este turno.
            </Alert>
            <TextField
              type="number"
              label="Efectivo contado al abrir el turno"
              value={openForm.openingAmount}
              onChange={(event) =>
                setOpenForm((current) => ({ ...current, openingAmount: event.target.value }))
              }
              inputProps={{ min: 0, step: "any" }}
            />
            <TextField
              label="Notas de apertura"
              multiline
              minRows={3}
              value={openForm.notes}
              onChange={(event) =>
                setOpenForm((current) => ({ ...current, notes: event.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenTarget(null)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<LockOpenRoundedIcon />}
            onClick={openRegister}
            disabled={saving}
          >
            {saving ? "Abriendo..." : "Abrir caja"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(closeTarget)}
        onClose={saving ? undefined : () => setCloseTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 950 }}>Cerrar {closeTarget?.register?.name}</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {closeTarget?.totals?.hidden || !canReadExpected ? (
              <Alert severity="info">
                Contá el efectivo físico e ingresá el total. El sistema no muestra el saldo esperado antes del arqueo.
              </Alert>
            ) : (
              <Alert severity="info">
                Saldo esperado:{" "}
                <strong>
                  {formatMoney(
                    closeTarget?.totals?.expected,
                    closeTarget?.register?.currency,
                  )}
                </strong>
              </Alert>
            )}
            <TextField
              type="number"
              label="Saldo contado y declarado"
              required
              autoFocus
              value={closeForm.declaredClosingAmount}
              onChange={(event) =>
                setCloseForm((current) => ({
                  ...current,
                  declaredClosingAmount: event.target.value,
                }))
              }
              inputProps={{ min: 0, step: "any" }}
              InputProps={{ readOnly: closeForm.useDenominations }}
              helperText={closeForm.useDenominations ? "Calculado a partir del desglose de billetes." : "Ingresá exactamente el dinero físico contado."}
            />
            {closeTarget?.register?.currency === "ARS" ? <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5 }}>
              <FormControlLabel
                control={<Switch checked={closeForm.useDenominations} onChange={(event) => setCloseForm((current) => ({
                  ...current,
                  useDenominations: event.target.checked,
                  declaredClosingAmount: event.target.checked ? String(cashCountTotal(current)) : current.declaredClosingAmount,
                }))} />}
                label="Contar por denominación de billetes"
              />
              {closeForm.useDenominations ? <>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>El total declarado se calcula automáticamente. Completá las cantidades; dejá 0 cuando no haya billetes.</Typography>
                <Grid container spacing={1.2}>
                  {CASH_DENOMINATIONS.map((bill) => <Grid item xs={6} sm={4} key={bill}>
                    <TextField size="small" fullWidth label={`$${bill.toLocaleString("es-AR")}`} type="number" value={closeForm.denominationCounts?.[bill] ?? ""} inputProps={{ min: 0, step: 1 }} onChange={(event) => setCloseForm((current) => {
                      const next = { ...current, denominationCounts: { ...current.denominationCounts, [bill]: event.target.value } };
                      return { ...next, declaredClosingAmount: String(cashCountTotal(next)) };
                    })} />
                  </Grid>)}
                  <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Monedas / otros billetes ($)" type="number" inputProps={{ min: 0, step: "0.01" }} value={closeForm.otherCashAmount} onChange={(event) => setCloseForm((current) => {
                    const next = { ...current, otherCashAmount: event.target.value };
                    return { ...next, declaredClosingAmount: String(cashCountTotal(next)) };
                  })} /></Grid>
                </Grid>
                <Typography sx={{ fontWeight: 900, mt: 1.5 }}>Total contado: {formatMoney(cashCountTotal(closeForm), closeTarget?.register?.currency)}</Typography>
              </> : null}
            </Paper> : null}
            <TextField
              label="Notas de cierre"
              multiline
              minRows={3}
              value={closeForm.notes}
              onChange={(event) =>
                setCloseForm((current) => ({ ...current, notes: event.target.value }))
              }
            />
            {!closeTarget?.totals?.hidden && canReadExpected && closeTarget?.totals?.expected != null ? (
              <Alert
                severity={
                  Number(closeForm.declaredClosingAmount || 0) ===
                  Number(closeTarget?.totals?.expected || 0)
                    ? "success"
                    : "warning"
                }
              >
                Diferencia estimada:{" "}
                <strong>
                  {formatMoney(
                    Number(closeForm.declaredClosingAmount || 0) -
                      Number(closeTarget?.totals?.expected || 0),
                    closeTarget?.register?.currency,
                  )}
                </strong>
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCloseTarget(null)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<LockRoundedIcon />}
            onClick={closeRegister}
            disabled={saving || closeForm.declaredClosingAmount === ""}
          >
            {saving ? "Cerrando..." : "Cerrar y arquear"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(operationTarget)}
        onClose={saving ? undefined : () => setOperationTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 950 }}>
          Operación en {operationTarget?.register?.name}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              label="Movimiento de caja"
              value={operationForm.direction}
              onChange={(event) =>
                setOperationForm((current) => ({
                  ...current,
                  direction: event.target.value,
                  itemId: "",
                  quantity: "1",
                  amount: "",
                  description: event.target.value === "in" ? cashPurposeDescription("owner_contribution") : cashPurposeDescription("owner_withdrawal"),
                  cashRecipient: "",
                  counterAccountCode:
                    event.target.value === "in" ? "OTHER_INCOME" : "OTHER_EXPENSE",
                  cashPurpose: event.target.value === "in" ? "owner_contribution" : "owner_withdrawal",
                }))
              }
            >
              {canCashIn ? (
                <MenuItem value="in">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CallReceivedRoundedIcon color="success" fontSize="small" />
                    <span>Ingreso</span>
                  </Stack>
                </MenuItem>
              ) : null}
              {canCashOut ? (
                <MenuItem value="out">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CallMadeRoundedIcon color="error" fontSize="small" />
                    <span>Egreso</span>
                  </Stack>
                </MenuItem>
              ) : null}
            </TextField>

            {canUseItemSelector ? (
              <Autocomplete
                options={selectableOperationItems}
                value={selectedOperationItem}
                loading={operationItemsLoading}
                getOptionLabel={itemOptionLabel}
                filterOptions={(options) => options}
                onInputChange={(_event, query, reason) => {
                  if (reason === "input" || reason === "clear") {
                    setOperationSearch(query);
                    setOperationPage(1);
                  }
                }}
                ListboxProps={{ onScroll: (event) => {
                  const list = event.currentTarget;
                  if (operationHasMore && !operationItemsLoading &&
                    list.scrollTop + list.clientHeight >= list.scrollHeight - 40) {
                    setOperationPage((page) => page + 1);
                  }
                } }}
                isOptionEqualToValue={(option, value) => entityId(option) === entityId(value)}
                noOptionsText={
                  operationItemsError ? "No se pudo buscar; probá de nuevo" : operationItemsLoading
                    ? "Cargando ítems..."
                    : operationForm.direction === "in"
                      ? "No hay ítems habilitados para vender"
                      : "No hay ítems habilitados para comprar o registrar como gasto"
                }
                onChange={(_event, item) => {
                  if (!item) {
                    setOperationForm((current) => ({
                      ...current,
                      itemId: "",
                      quantity: "1",
                      amount: "",
                      description: cashPurposeDescription(current.cashPurpose),
                    }));
                    return;
                  }
                  const accounting = item.accounting || {};
                  const amount = suggestedOperationAmount(item, operationForm.direction);
                  const code = String(item.codigo || item.code || "").trim();
                  const operationName = operationForm.direction === "in"
                    ? "Venta"
                    : itemCapabilities(item).stockTracked
                      ? "Compra"
                      : "Gasto";
                  setOperationForm((current) => ({
                    ...current,
                    itemId: entityId(item),
                    quantity: "1",
                    amount,
                    description: `${operationName} · ${item.nombre || item.name || "Ítem"}`,
                    counterAccountCode:
                      operationForm.direction === "in"
                        ? accounting.salesAccountCode || "SALES"
                        : itemCapabilities(item).stockTracked
                          ? accounting.inventoryAccountCode || "INVENTORY"
                          : accounting.expenseAccountCode ||
                            accounting.purchaseAccountCode ||
                            "OTHER_EXPENSE",
                    reference: current.reference || code,
                  }));
                }}
                renderOption={(props, option) => {
                  const capabilities = itemCapabilities(option);
                  const taxCode = itemTaxCode(option);
                  const taxProfile = (bootstrap.taxProfiles || []).find(
                    (profile) => String(profile.code || "").toUpperCase() === taxCode,
                  );
                  return (
                    <Box component="li" {...props} key={entityId(option)}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 850 }} noWrap>
                          {option.nombre || option.name || "Ítem"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }} noWrap>
                          {ITEM_KIND_LABELS[itemKindOf(option)] || "Ítem"}
                          {option.categoria || option.category
                            ? ` · ${option.categoria || option.category}`
                            : ""}
                          {capabilities.stockTracked
                            ? ` · existencia ${Number(option.stock || 0)}`
                            : ""}
                          {taxProfile
                            ? ` · preparado para facturar con ${taxProfile.name || "impuesto configurado"}`
                            : " · sin configuración fiscal"}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ítem asociado"
                    placeholder="Buscá por nombre, código, SKU o categoría"
                    helperText={
                      operationForm.direction === "in"
                        ? "Opcional. Al elegir un ítem se registra una venta cobrada y, si corresponde, se descuenta inventario."
                        : "Opcional. Al elegir un ítem se registra una compra o gasto pagado y, si corresponde, se suma inventario."
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {operationItemsLoading ? (
                            <WebProfitSpinner color="inherit" size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            ) : (
              <Alert severity="info">
                Podés registrar un movimiento manual. Para asociar ítems, tu rol también necesita permiso para
                {operationForm.direction === "in" ? " vender y cobrar" : " comprar o registrar gastos y pagarlos"}.
              </Alert>
            )}

            {operationItemsError ? (
              <Alert severity="warning">
                {operationItemsError} Podés continuar con un movimiento manual.
              </Alert>
            ) : null}

            {selectedOperationItem ? (
              <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3 }}>
                <Stack spacing={1.2}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
                        {selectedOperationItem.nombre || selectedOperationItem.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                        {operationForm.direction === "in"
                          ? "Se registrará como venta cobrada en esta caja."
                          : selectedCapabilities?.stockTracked
                            ? "Se registrará como compra pagada y aumentará el inventario."
                            : "Se registrará como gasto pagado desde esta caja."}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      color={selectedTaxProfile ? "success" : "warning"}
                      label={selectedTaxProfile ? "Preparado para facturar" : "Falta preparar facturación"}
                      sx={{ fontWeight: 850, alignSelf: "flex-start" }}
                    />
                  </Stack>

                  <Alert severity={selectedTaxProfile ? "success" : "warning"} icon={false}>
                    {selectedTaxProfile ? (
                      <>
                        <strong>Configuración impositiva lista:</strong> {selectedTaxProfile.name || "Impuesto configurado"}
                        {Number(selectedTaxProfile.rate || 0) > 0
                          ? selectedTaxProfile.calculationMode === "fixed"
                            ? ` (${formatMoney(Number(selectedTaxProfile.rate), operationTarget?.register?.currency)} por operación)`
                            : ` (${Number(selectedTaxProfile.rate)}%)`
                          : ""}. La venta o compra guardará este cálculo. La emisión de una factura fiscal depende de que el proyecto tenga un proveedor de facturación conectado.
                      </>
                    ) : (
                      <>
                        <strong>Este ítem no tiene una configuración impositiva.</strong> La operación se guardará sin impuesto. Antes de facturarla fiscalmente, un responsable deberá completar esa configuración y contar con un proveedor de facturación conectado.
                      </>
                    )}
                  </Alert>

                  {!selectedTaxProfile ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        navigate(
                          `/dashboard/proyectos/${projectId}/sistema/items?editar=${encodeURIComponent(
                            entityId(selectedOperationItem),
                          )}`,
                        )
                      }
                      sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 850 }}
                    >
                      Configurar impuestos del ítem
                    </Button>
                  ) : null}

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                      <strong>Cuenta:</strong> {selectedAccount?.name || "Cuenta general"}
                    </Typography>
                    {selectedCapabilities?.stockTracked ? (
                      <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                        <strong>Existencia:</strong> {Number(selectedOperationItem.stock || 0)} {selectedOperationItem.unidad || selectedOperationItem.unit || "unidades"}
                      </Typography>
                    ) : null}
                    {itemKindOf(selectedOperationItem) === "service" && itemDuration(selectedOperationItem) > 0 ? (
                      <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                        <strong>Duración:</strong> {itemDuration(selectedOperationItem)} minutos
                      </Typography>
                    ) : null}
                  </Stack>

                  {canReadItemCosts &&
                  operationForm.direction === "in" &&
                  selectedCapabilities?.stockTracked &&
                  Number(selectedOperationItem.costing?.averageCost || selectedOperationItem.costing?.lastCost || 0) <= 0 ? (
                    <Alert severity="warning" icon={false}>
                      Falta cargar el costo del ítem. La venta descontará inventario, pero el margen y el costo vendido no serán confiables.
                    </Alert>
                  ) : null}
                </Stack>
              </Paper>
            ) : null}

            <Grid container spacing={1.5}>
              {selectedOperationItem ? (
                <Grid item xs={12} sm={4}>
                  <TextField
                    type="number"
                    label="Cantidad"
                    required
                    fullWidth
                    value={operationForm.quantity}
                    onChange={(event) =>
                      setOperationForm((current) => ({ ...current, quantity: event.target.value }))
                    }
                    inputProps={{ min: 0.0001, step: "any" }}
                  />
                </Grid>
              ) : null}
              <Grid item xs={12} sm={selectedOperationItem ? 8 : 12}>
                <TextField
                  type="number"
                  label={selectedOperationItem ? "Importe unitario" : "Importe total"}
                  required
                  fullWidth
                  value={operationForm.amount}
                  onChange={(event) =>
                    setOperationForm((current) => ({ ...current, amount: event.target.value }))
                  }
                  inputProps={{ min: 0, step: "any" }}
                />
              </Grid>
            </Grid>

            {selectedOperationItem && unitAmountValue > 0 ? (
              <Alert severity="info" icon={false}>
                Total estimado: <strong>{formatMoney(estimatedOperationTotal, operationTarget?.register?.currency)}</strong>
                {estimatedTax > 0
                  ? taxIncluded
                    ? ` · incluye ${formatMoney(estimatedTax, operationTarget?.register?.currency)} de impuesto.`
                    : ` · se agregan ${formatMoney(estimatedTax, operationTarget?.register?.currency)} de impuesto.`
                  : "."}
              </Alert>
            ) : null}

            <TextField
              label="Descripción"
              required
              value={operationForm.description}
              onChange={(event) =>
                setOperationForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
            />

            {!selectedOperationItem ? (
              <>
                <TextField
                  select
                  label="¿Por qué entra o sale esta plata?"
                  value={operationForm.cashPurpose}
                  onChange={(event) => setOperationForm((current) => ({
                    ...current,
                    cashPurpose: event.target.value,
                    description: !current.description.trim() || current.description === cashPurposeDescription(current.cashPurpose)
                      ? cashPurposeDescription(event.target.value) : current.description,
                  }))}
                  helperText="La caja indica dónde está el dinero. El motivo distingue un aporte o retiro del dueño de un ingreso o gasto del negocio."
                >
                  {operationForm.direction === "in" ? [
                    <MenuItem key="owner_contribution" value="owner_contribution">Aporte del dueño (no es venta)</MenuItem>,
                    <MenuItem key="other_income" value="other_income">Otro ingreso real del negocio</MenuItem>,
                    ...(hasProjectPermission(access, "erp.ledger.manage") ? [
                      <MenuItem key="custom" value="">Otra cuenta contable (avanzado)</MenuItem>,
                    ] : []),
                  ] : [
                    <MenuItem key="owner_withdrawal" value="owner_withdrawal">Retiro del dueño (no es gasto operativo)</MenuItem>,
                    <MenuItem key="business_expense" value="business_expense">Gasto real del negocio</MenuItem>,
                    <MenuItem key="employee_advance" value="employee_advance">Dinero entregado para rendir después</MenuItem>,
                    ...(hasProjectPermission(access, "erp.ledger.manage") ? [
                      <MenuItem key="custom" value="">Otra cuenta contable (avanzado)</MenuItem>,
                    ] : []),
                  ]}
                </TextField>
                {["owner_withdrawal", "employee_advance"].includes(operationForm.cashPurpose) ? (
                  <TextField required label="¿Quién recibió o retiró el dinero?"
                    value={operationForm.cashRecipient}
                    onChange={(event) => setOperationForm((current) => ({ ...current, cashRecipient: event.target.value }))}
                    inputProps={{ maxLength: 180 }}
                    helperText="Se guarda en el movimiento para identificar al responsable."
                  />
                ) : null}
                {operationForm.cashPurpose === "employee_advance" ? (
                  <Alert severity="info">Esto no se considera un gasto hasta que se registre la rendición correspondiente.</Alert>
                ) : null}
                {operationForm.cashPurpose === "" && hasProjectPermission(access, "erp.ledger.manage") ? (
                  <TextField select label="Categoría contable (avanzado)" value={operationForm.counterAccountCode}
                    onChange={(event) => setOperationForm((current) => ({ ...current, counterAccountCode: event.target.value }))}>
                    {(bootstrap.accounts || []).filter((account) => account.active !== false &&
                      (operationForm.direction === "in" ? ["income", "liability", "equity"] : ["expense", "asset", "liability", "equity"]).includes(account.type))
                      .map((account) => <MenuItem key={account.code} value={account.code}>{account.name} · {ACCOUNT_TYPE_LABELS[account.type]}</MenuItem>)}
                  </TextField>
                ) : null}
              </>
            ) : null}

            <TextField
              label="Número o referencia"
              value={operationForm.reference}
              onChange={(event) =>
                setOperationForm((current) => ({
                  ...current,
                  reference: event.target.value,
                }))
              }
            />
            <TextField
              label="Notas internas"
              multiline
              minRows={2}
              value={operationForm.notes}
              onChange={(event) =>
                setOperationForm((current) => ({ ...current, notes: event.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOperationTarget(null)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={operationForm.direction === "in" ? "success" : "error"}
            onClick={recordOperation}
            disabled={saving}
          >
            {saving ? "Registrando..." : "Registrar operación"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(reviewTarget)}
        onClose={saving ? undefined : () => setReviewTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 950 }}>
          Revisar cierre {reviewTarget?.sessionNumber}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity={Number(reviewTarget?.difference || 0) === 0 ? "success" : "warning"}>
              Diferencia registrada:{" "}
              <strong>{formatMoney(reviewTarget?.difference, reviewTarget?.currency)}</strong>
            </Alert>
            <TextField
              select
              label="Resultado de revisión"
              value={reviewForm.reviewStatus}
              onChange={(event) =>
                setReviewForm((current) => ({
                  ...current,
                  reviewStatus: event.target.value,
                }))
              }
            >
              <MenuItem value="approved">Aprobar cierre</MenuItem>
              <MenuItem value="observed">Marcar como observado</MenuItem>
            </TextField>
            <TextField
              label="Notas de revisión"
              multiline
              minRows={3}
              value={reviewForm.reviewNotes}
              onChange={(event) =>
                setReviewForm((current) => ({
                  ...current,
                  reviewNotes: event.target.value,
                }))
              }
            />
            {Number(reviewTarget?.difference || 0) !== 0 && canCreateReviewAdjustment && !reviewTarget?.adjustmentMovementId ? (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={reviewForm.createAdjustment}
                      onChange={(event) =>
                        setReviewForm((current) => ({
                          ...current,
                          createAdjustment: event.target.checked,
                        }))
                      }
                    />
                  }
                  label="Crear ajuste contable por la diferencia"
                />
                {reviewForm.createAdjustment ? (
                  <TextField
                    label="Motivo del ajuste"
                    multiline
                    minRows={2}
                    value={reviewForm.adjustmentNotes}
                    onChange={(event) =>
                      setReviewForm((current) => ({
                        ...current,
                        adjustmentNotes: event.target.value,
                      }))
                    }
                  />
                ) : null}
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReviewTarget(null)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <WebProfitSpinner size={16} /> : <FactCheckRoundedIcon />}
            onClick={reviewSession}
            disabled={saving}
          >
            {saving ? "Guardando..." : "Confirmar revisión"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
